import React, { useEffect, useState } from 'react';
import { analyticsAPI, sensorAPI } from '../services/api';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, TrendingDown, Droplets, Activity, AlertTriangle, WifiIcon, Clock, Database } from 'lucide-react';
import { format, subHours, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import { SensorData } from '../types';

interface AnalyticsViewProps {
  selectedDeviceId: string | null;
  onSelectDevice?: (deviceId: string) => void;
}

const AnalyticsImproved: React.FC<AnalyticsViewProps> = ({ selectedDeviceId, onSelectDevice }) => {
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [recommendation, setRecommendation] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  const [hasData, setHasData] = useState(false);
  const [availableSensors, setAvailableSensors] = useState<SensorData[]>([]);
  const [localSelectedDevice, setLocalSelectedDevice] = useState<string | null>(selectedDeviceId);

  // Fetch available sensors on mount
  useEffect(() => {
    fetchAvailableSensors();
  }, []);

  // Auto-select first sensor if none selected
  useEffect(() => {
    if (!localSelectedDevice && availableSensors.length > 0) {
      const firstSensor = availableSensors[0].deviceId;
      setLocalSelectedDevice(firstSensor);
      if (onSelectDevice) {
        onSelectDevice(firstSensor);
      }
    }
  }, [availableSensors, localSelectedDevice, onSelectDevice]);

  // Sync with parent selection
  useEffect(() => {
    if (selectedDeviceId) {
      setLocalSelectedDevice(selectedDeviceId);
    }
  }, [selectedDeviceId]);

  useEffect(() => {
    if (localSelectedDevice) {
      fetchAnalytics();
    }
  }, [localSelectedDevice, selectedPeriod]);

  const fetchAvailableSensors = async () => {
    try {
      const response = await sensorAPI.getLatest();
      if (response.data && Array.isArray(response.data)) {
        setAvailableSensors(response.data);
      }
    } catch (error) {
      console.error('Error fetching sensors:', error);
    }
  };

  const handleSelectSensor = (deviceId: string) => {
    setLocalSelectedDevice(deviceId);
    if (onSelectDevice) {
      onSelectDevice(deviceId);
    }
  };

  const fetchAnalytics = async () => {
    if (!localSelectedDevice) return;

    try {
      setLoading(true);
      setHasData(false);

      // Calculate date range based on period
      const now = new Date();
      let from;
      switch (selectedPeriod) {
        case '1h':
          from = subHours(now, 1).toISOString();
          break;
        case '6h':
          from = subHours(now, 6).toISOString();
          break;
        case '24h':
          from = subHours(now, 24).toISOString();
          break;
        case '7d':
          from = subDays(now, 7).toISOString();
          break;
        default:
          from = subHours(now, 24).toISOString();
      }

      // Fetch historical data
      const historyRes = await sensorAPI.getHistory(localSelectedDevice, from);
      
      if (historyRes.data && historyRes.data.length > 0) {
        setHasData(true);
        
        // Format data for charts
        const formattedData = historyRes.data.map((item: any) => ({
          time: format(new Date(item.timestamp), 'HH:mm'),
          date: format(new Date(item.timestamp), 'dd/MM HH:mm'),
          moisture: item.moistureValue || item.sensors?.soilMoisture?.value || 0,
          quality: (item.quality || item.sensors?.soilMoisture?.quality || 0) * 100,
          wifiSignal: item.wifiSignal || item.system?.wifi_signal || 0
        }));
        
        setHistoryData(formattedData);

        // Calculate statistics
        const moistureValues = formattedData.map((d: any) => d.moisture);
        const avgMoisture = moistureValues.reduce((a: number, b: number) => a + b, 0) / moistureValues.length;
        const minMoisture = Math.min(...moistureValues);
        const maxMoisture = Math.max(...moistureValues);
        
        setStats({
          dataPoints: formattedData.length,
          average: avgMoisture.toFixed(1),
          min: minMoisture.toFixed(1),
          max: maxMoisture.toFixed(1),
          trend: moistureValues[moistureValues.length - 1] > moistureValues[0] ? 'increasing' : 'decreasing'
        });

        // Generate simple recommendation
        if (avgMoisture < 30) {
          setRecommendation({
            action: 'IRRIGATION URGENTE',
            urgency: 'critical',
            message: `L'humidité moyenne est très faible (${avgMoisture.toFixed(1)}%). Irrigation immédiate recommandée.`,
            estimatedAmount: `${Math.round((60 - avgMoisture) * 10)}L`
          });
        } else if (avgMoisture < 45) {
          setRecommendation({
            action: 'IRRIGATION RECOMMANDÉE',
            urgency: 'high',
            message: `L'humidité est en dessous du niveau optimal (${avgMoisture.toFixed(1)}%). Planifier une irrigation prochainement.`,
            estimatedAmount: `${Math.round((60 - avgMoisture) * 10)}L`
          });
        } else if (avgMoisture < 60) {
          setRecommendation({
            action: 'NIVEAU OPTIMAL',
            urgency: 'low',
            message: `L'humidité est dans la plage optimale (${avgMoisture.toFixed(1)}%). Continuer la surveillance.`,
            estimatedAmount: 'Aucune action nécessaire'
          });
        } else {
          setRecommendation({
            action: 'HUMIDITÉ ÉLEVÉE',
            urgency: 'medium',
            message: `L'humidité est élevée (${avgMoisture.toFixed(1)}%). Surveiller pour éviter l'excès d'eau.`,
            estimatedAmount: 'Aucune irrigation nécessaire'
          });
        }
      } else {
        setHasData(false);
        toast('Pas assez de données historiques disponibles. Les données s\'accumuleront au fil du temps.', {
          icon: 'ℹ️',
        });
      }
    } catch (error: any) {
      console.error('Error fetching analytics:', error);
      setHasData(false);
      if (error.response?.status === 404) {
        toast('Données historiques non disponibles. Le système collecte des données...', {
          icon: 'ℹ️',
        });
      } else {
        toast.error('Erreur lors de la récupération des analyses');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationColor = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return 'rgb(220, 38, 38)';
      case 'high':
        return 'rgb(234, 88, 12)';
      case 'medium':
        return 'rgb(234, 179, 8)';
      case 'low':
        return 'rgb(34, 197, 94)';
      default:
        return 'rgb(59, 130, 246)';
    }
  };

  // Show sensor selection UI if no sensor is selected
  if (!localSelectedDevice) {
    if (availableSensors.length === 0) {
      return (
        <div className="card" style={{textAlign: 'center', padding: '3rem'}}>
          <Database style={{width: '4rem', height: '4rem', color: '#9ca3af', margin: '0 auto 1rem'}} />
          <h3 style={{color: 'var(--text-primary)', marginBottom: '0.5rem'}}>
            Aucun capteur détecté
          </h3>
          <p style={{color: 'var(--text-secondary)'}}>
            Le système recherche des capteurs actifs. Veuillez patienter...
          </p>
        </div>
      );
    }

    return (
      <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
        <div className="card">
          <h2 className="card-title" style={{marginBottom: '0.5rem'}}>📊 Analyses de Capteurs</h2>
          <p style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>
            Sélectionnez un capteur pour afficher ses analyses détaillées
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableSensors.map((sensor) => {
            const moisture = sensor.sensors?.soilMoisture;
            const status = moisture?.status || 'unknown';
            
            return (
              <div
                key={sensor.deviceId}
                onClick={() => handleSelectSensor(sensor.deviceId)}
                className="sensor-card"
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '2px solid transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--primary)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    margin: 0
                  }}>
                    📡 {sensor.deviceId}
                  </h3>
                  <span className={`status-badge status-${status}`}>
                    {status === 'normal' ? 'Normal' : status === 'warning' ? 'Attention' : 'Critique'}
                  </span>
                </div>

                {moisture && (
                  <div style={{marginBottom: '1rem'}}>
                    <div className="flex items-center justify-between">
                      <span style={{fontSize: '0.875rem', color: 'var(--text-secondary)'}}>
                        💧 Humidité
                      </span>
                      <span style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: moisture.value < 30 ? '#ef4444' : moisture.value < 50 ? '#f59e0b' : '#10b981'
                      }}>
                        {moisture.value.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}

                <button
                  className="btn-primary"
                  style={{width: '100%', marginTop: '0.5rem'}}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectSensor(sensor.deviceId);
                  }}
                >
                  Voir les analyses
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '24rem'
      }}>
        <div>
          <div style={{
            width: '3rem',
            height: '3rem',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{color: 'var(--text-secondary)'}}>Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
      {/* Header */}
      <div className="card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem'}}>
          <div>
            <h2 className="card-title" style={{marginBottom: '0.25rem'}}>📊 Analyses - {localSelectedDevice}</h2>
            <p style={{fontSize: '0.75rem', color: 'var(--text-tertiary)', margin: 0}}>
              {availableSensors.length} capteur(s) disponible(s)
            </p>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid var(--border-light)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            <option value="1h">Dernière heure</option>
            <option value="6h">Dernières 6 heures</option>
            <option value="24h">Dernières 24 heures</option>
            <option value="7d">Derniers 7 jours</option>
          </select>
        </div>
      </div>

      {!hasData ? (
        <div className="card" style={{textAlign: 'center', padding: '3rem'}}>
          <Database style={{width: '4rem', height: '4rem', color: '#9ca3af', margin: '0 auto 1rem'}} />
          <h3 style={{color: 'var(--text-primary)', marginBottom: '0.5rem'}}>
            Données historiques insuffisantes
          </h3>
          <p style={{color: 'var(--text-secondary)', marginBottom: '1rem'}}>
            Le système collecte actuellement des données. Les analyses seront disponibles une fois qu'il y aura suffisamment de points de données historiques.
          </p>
          <p style={{
            fontSize: '0.875rem',
            color: 'var(--text-secondary)',
            padding: '1rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '0.5rem',
            display: 'inline-block'
          }}>
            💡 Les données sont collectées toutes les ~7-8 secondes. Revenez dans quelques minutes pour voir les tendances.
          </p>
        </div>
      ) : (
        <>
          {/* Recommendation Card */}
          {recommendation && (
            <div className="card" style={{
              borderLeft: `4px solid ${getRecommendationColor(recommendation.urgency)}`,
              backgroundColor: 'var(--bg-primary)'
            }}>
              <div style={{display: 'flex', alignItems: 'start', gap: '1rem'}}>
                <Droplets style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  color: getRecommendationColor(recommendation.urgency),
                  flexShrink: 0
                }} />
                <div style={{flex: 1}}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    color: getRecommendationColor(recommendation.urgency),
                    marginBottom: '0.5rem'
                  }}>
                    {recommendation.action}
                  </h3>
                  <p style={{color: 'var(--text-primary)', marginBottom: '0.5rem'}}>
                    {recommendation.message}
                  </p>
                  <p style={{fontSize: '0.875rem', color: 'var(--text-secondary)'}}>
                    <strong>Estimation:</strong> {recommendation.estimatedAmount}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Statistics Cards */}
          {stats && (
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem'}}>
              <div className="card" style={{textAlign: 'center'}}>
                <Database style={{width: '2rem', height: '2rem', color: '#3b82f6', margin: '0 auto 0.5rem'}} />
                <p style={{fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Points de données
                </p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--text-primary)'}}>
                  {stats.dataPoints}
                </p>
              </div>
              
              <div className="card" style={{textAlign: 'center'}}>
                <Activity style={{width: '2rem', height: '2rem', color: '#10b981', margin: '0 auto 0.5rem'}} />
                <p style={{fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Humidité moy.
                </p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--text-primary)'}}>
                  {stats.average}%
                </p>
              </div>
              
              <div className="card" style={{textAlign: 'center'}}>
                <TrendingDown style={{width: '2rem', height: '2rem', color: '#ef4444', margin: '0 auto 0.5rem'}} />
                <p style={{fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Minimum
                </p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--text-primary)'}}>
                  {stats.min}%
                </p>
              </div>
              
              <div className="card" style={{textAlign: 'center'}}>
                <TrendingUp style={{width: '2rem', height: '2rem', color: '#8b5cf6', margin: '0 auto 0.5rem'}} />
                <p style={{fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Maximum
                </p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--text-primary)'}}>
                  {stats.max}%
                </p>
              </div>
            </div>
          )}

          {/* Main Chart */}
          <div className="card">
            <h3 className="card-title" style={{marginBottom: '1rem'}}>
              📈 Évolution de l'Humidité du Sol
            </h3>
            <div style={{height: '400px'}}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historyData}>
                  <defs>
                    <linearGradient id="moistureGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis 
                    dataKey="time" 
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    domain={[0, 100]}
                    label={{ value: 'Humidité (%)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem'
                    }}
                    labelFormatter={(value) => `Heure: ${value}`}
                    formatter={(value: any) => [`${value.toFixed(1)}%`, 'Humidité']}
                  />
                  <Area
                    type="monotone"
                    dataKey="moisture"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#moistureGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Quality and Signal Chart */}
          <div className="card">
            <h3 className="card-title" style={{marginBottom: '1rem'}}>
              📊 Qualité du Signal et des Données
            </h3>
            <div style={{height: '300px'}}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={historyData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis 
                    dataKey="time" 
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fontSize: 12 }}
                    domain={[0, 100]}
                    label={{ value: 'Qualité (%)', angle: -90, position: 'insideLeft' }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Signal WiFi (dBm)', angle: 90, position: 'insideRight' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem'
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="quality"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                    name="Qualité des données"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="wifiSignal"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={false}
                    name="Signal WiFi"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsImproved;
